import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { FolderKanban, Users, Send, Inbox, ArrowRight, CheckCircle2, Circle, Mail, Cloud } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ projects: 0, subs: 0, itbs: 0, routed: 0 });
  const [status, setStatus] = useState({ email: false, box: false, root: false, subs: false });

  useEffect(() => {
    Promise.all([
      api.get("/projects"), api.get("/subcontractors"), api.get("/itbs"), api.get("/routing-logs"),
      api.get("/email/connection"), api.get("/storage/status"), api.get("/settings/routing"),
    ]).then(([p, s, i, r, e, b, rt]) => {
      setStats({
        projects: p.data.length, subs: s.data.length, itbs: i.data.length,
        routed: r.data.filter((x) => x.status === "routed").length,
      });
      setStatus({
        email: !!e.data?.connected, box: !!b.data?.connected,
        root: !!rt.data?.projects_root_folder_id, subs: s.data.length > 0,
      });
    }).catch(() => {});
  }, []);

  const cards = [
    { label: "Projects", value: stats.projects, icon: FolderKanban, to: "/projects" },
    { label: "Subcontractors", value: stats.subs, icon: Users, to: "/subcontractors" },
    { label: "ITBs Sent", value: stats.itbs, icon: Send, to: "/send-itb" },
    { label: "PDFs Routed", value: stats.routed, icon: Inbox, to: "/inbox" },
  ];

  const steps = [
    { key: "email", n: 1, title: "Connect your email inbox", desc: "Pick Google, Outlook, or IMAP in Settings — we send ITBs and capture replies from here.", to: "/settings", done: status.email },
    { key: "box", n: 2, title: "Connect your storage", desc: "Authorize Box or SharePoint so routed PDFs are filed straight into your drive.", to: "/settings", done: status.box },
    { key: "root", n: 3, title: "Set your Projects Root & detect projects", desc: "Point us at your projects folder; we auto-detect each project.", to: "/settings", done: status.root },
    { key: "subs", n: 4, title: "Build your subcontractor directory", desc: "Add subs (or click ‘Add sample subs’) and tag them with CSI codes.", to: "/subcontractors", done: status.subs },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <>
      <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || ""}`} description={user?.company_name} />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <Link to={c.to} key={c.label}>
              <Card className="p-5 border border-border hover:shadow-sm transition-shadow" data-testid={`stat-${c.label.toLowerCase().replace(/ /g, "-")}`}>
                <c.icon className="h-5 w-5 text-primary" />
                <p className="mt-4 font-display font-black text-4xl tracking-tight">{c.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{c.label}</p>
              </Card>
            </Link>
          ))}
        </div>

        {doneCount < steps.length && (
          <Card className="p-6 border border-border" data-testid="onboarding-checklist">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display font-bold text-lg">Get set up</h2>
              <span className="text-sm text-muted-foreground">{doneCount} of {steps.length} complete</span>
            </div>
            <p className="text-sm text-muted-foreground mb-3">Follow these steps in order — each links to exactly where you need to go.</p>
            <Progress value={pct} className="h-2 mb-5" />
            <div className="grid md:grid-cols-2 gap-3">
              {steps.map((s) => (
                <Link to={s.to} key={s.key}>
                  <div className={`p-4 rounded-md border flex items-start gap-3 transition-colors group ${s.done ? "border-emerald-200 bg-emerald-50/50" : "border-border hover:border-primary"}`} data-testid={`onboard-${s.key}`}>
                    {s.done ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />}
                    <div className="flex-1">
                      <p className={`font-semibold text-sm ${s.done ? "line-through text-muted-foreground" : ""}`}>{s.n}. {s.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{s.desc}</p>
                    </div>
                    {!s.done && <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors mt-0.5" />}
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}

        {doneCount === steps.length && (
          <Card className="p-6 border border-border flex items-center gap-4" data-testid="onboarding-complete">
            <div className="h-10 w-10 rounded-md bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="h-6 w-6 text-emerald-600" /></div>
            <div className="flex-1">
              <p className="font-semibold">You're all set up.</p>
              <p className="text-sm text-muted-foreground">Inbox and storage are connected. Head to <Link to="/send-itb" className="text-primary font-medium">Send ITB</Link> to dispatch invitations, or <Link to="/inbox" className="text-primary font-medium">Inbox Routing</Link> to watch replies auto-file.</p>
            </div>
          </Card>
        )}

        <Card className="p-5 border border-border flex items-start gap-3 bg-secondary/40">
          <div className="flex items-center gap-2 shrink-0">
            <Mail className={`h-4 w-4 ${status.email ? "text-emerald-600" : "text-muted-foreground"}`} />
            <Cloud className={`h-4 w-4 ${status.box ? "text-emerald-600" : "text-muted-foreground"}`} />
          </div>
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">Your data is secure.</span> Credentials are encrypted at rest, sessions use HTTP-only cookies, and every company workspace is fully isolated. See the Security section in <Link to="/settings" className="text-primary">Settings</Link>.
          </p>
        </Card>
      </div>
    </>
  );
}
